import {createStyles, Select, Stack, Text} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';

import deriveCapabilities from './eventTriggerConfigForm/deriveCapabilities';
import EventTypeSection from './eventTriggerConfigForm/EventTypeSection';
import TargetScopeSection from './eventTriggerConfigForm/TargetScopeSection';
import {TriggerScope} from './eventTriggerConfigForm/types';
import {AllowedEventTrigger, EventTriggerConfig} from './RoutineTypes';
import {getTitleCasedObjectTypeLabel} from './routineUtils';
import {FIELD_CHANGED, OBJECT_CREATED, TriggerKind} from './triggerConstants';

const SELECT_DROPDOWN_OFFSET_PX = 12;

interface EventTriggerConfigFormProps {
  config: Partial<EventTriggerConfig>;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  allowedTriggers: Array<AllowedEventTrigger>;
}

const useStyles = createStyles((theme) => ({
  section: {
    gap: theme.spacing.xs,
  },
  selectDropdown: {
    // Select does not expose its internal Popover offset. Cancel Mantine's
    // default 8px offset plus the Nucleus default 8px arrow adjustment.
    '&[data-position^="bottom"]': {
      marginTop: -SELECT_DROPDOWN_OFFSET_PX,
    },
    '&[data-position^="top"]': {
      marginTop: SELECT_DROPDOWN_OFFSET_PX,
    },
  },
  card: {
    cursor: 'pointer',
  },
  selectedCard: {
    backgroundColor: theme.colors.blue[0],
    borderColor: theme.colors.blue[7],
  },
  disabledCard: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  searchInputContainer: {
    marginLeft: theme.spacing.lg,
    maxWidth: 400,
    // Align SearchInput disabled surface with Mantine disabled inputs in this form.
    '& .chipSelectInput.is-disabled': {
      backgroundColor: theme.colors.gray[1],
      borderColor: theme.colors.gray[4],
    },
    // ChipSelectInput renders an inner disabled input with its own gray background.
    // Make it transparent so the outer disabled surface matches Mantine inputs.
    '& .chipSelectInput.is-disabled .chipSelectInput-input': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  },
  nestedEventInputContainer: {
    marginTop: theme.spacing.xs,
  },
}));

interface ObjectTypeUIConfig {
  pluralLabel: string;
  searchType: string;
  schemaFolderItemType?: OligoAwareFolderItemType;
}

// TODO (BNCH-154854): Centralize this configuration in one place or make it dynamic.
// Keys must match the PascalCase object types from EVENT_TRIGGER_REGISTRY on the backend.
const OBJECT_TYPE_UI_CONFIG: Record<string, ObjectTypeUIConfig> = {
  Analysis: {pluralLabel: 'analyses', searchType: ModelTypeConstants.ANALYSIS},
  CustomEntity: {
    pluralLabel: 'custom entities',
    searchType: ModelTypeConstants.CUSTOM_ENTITY,
    schemaFolderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
  },
  Entry: {
    pluralLabel: 'entries',
    searchType: ModelTypeConstants.ENTRY,
    schemaFolderItemType: ModelTypeConstants.ENTRY,
  },
  Mixture: {
    pluralLabel: 'mixtures',
    searchType: ModelTypeConstants.MIXTURE,
    schemaFolderItemType: ModelTypeConstants.MIXTURE,
  },
  Molecule: {
    pluralLabel: 'molecules',
    searchType: ModelTypeConstants.MOLECULE,
    schemaFolderItemType: ModelTypeConstants.MOLECULE,
  },
  Protein: {
    pluralLabel: 'proteins',
    searchType: ModelTypeConstants.PROTEIN,
    schemaFolderItemType: ModelTypeConstants.PROTEIN,
  },
  Study: {pluralLabel: 'studies', searchType: ModelTypeConstants.STUDY},
};

function getObjectTypeUIConfig(objectType: string): ObjectTypeUIConfig | undefined {
  return OBJECT_TYPE_UI_CONFIG[objectType];
}

function getModelTypeConstantForObjectType(objectType: string): string | null {
  return getObjectTypeUIConfig(objectType)?.searchType ?? null;
}

function deriveInitialScope(config: Partial<EventTriggerConfig>): TriggerScope | undefined {
  if (config.object_ids != null) {
    return 'instance';
  }
  if (config.schema_id != null) {
    return 'schema';
  }
  if ((config.object_type ?? '') !== '' && config.trigger_kind != null) {
    return 'all';
  }
  return undefined;
}

export default function EventTriggerConfigForm({
  config,
  onChange,
  allowedTriggers,
}: EventTriggerConfigFormProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [selectedScope, setSelectedScope] = useState<TriggerScope | undefined>(() =>
    deriveInitialScope(config)
  );

  const objectTypeSelectData = useMemo(
    () =>
      allowedTriggers.map((trigger) => ({
        value: trigger.objectType,
        label: getTitleCasedObjectTypeLabel(trigger.objectType),
      })),
    [allowedTriggers]
  );

  const triggerByObjectType = useMemo(() => {
    const byObjectType = new Map<string, AllowedEventTrigger>();
    for (const trigger of allowedTriggers) {
      byObjectType.set(trigger.objectType, trigger);
    }
    return byObjectType;
  }, [allowedTriggers]);

  const selectedObjectType = config.object_type ?? '';
  const selectedTrigger = selectedObjectType === '' ? undefined : triggerByObjectType.get(selectedObjectType);
  const selectedObjectTypeUIConfig = getObjectTypeUIConfig(selectedObjectType);
  const selectedPluralObjectTypeLabel = selectedObjectTypeUIConfig?.pluralLabel ?? 'objects';
  const selectedObjectSearchType =
    getModelTypeConstantForObjectType(selectedObjectType) ?? selectedObjectType;
  const selectedSchemaFolderItemType = useMemo(() => {
    if (selectedTrigger?.schemaSearchType == null) {
      return null;
    }
    return selectedObjectTypeUIConfig?.schemaFolderItemType ?? null;
  }, [selectedObjectTypeUIConfig, selectedTrigger]);

  const objectTypeData = useMemo(() => {
    const objectType = config.object_type ?? '';
    if (objectType === '' || objectTypeSelectData.some((option) => option.value === objectType)) {
      return objectTypeSelectData;
    }
    return [{value: objectType, label: getTitleCasedObjectTypeLabel(objectType)}, ...objectTypeSelectData];
  }, [config.object_type, objectTypeSelectData]);

  const fieldOptions = useMemo(
    () => (selectedTrigger?.fields ?? []).map((field) => ({value: field.field, label: field.displayName})),
    [selectedTrigger]
  );

  const selectedField = selectedTrigger?.fields.find((field) => field.field === config.field);

  const valueOptions = useMemo(
    () => (selectedField?.allowedValues ?? []).map((value) => ({value, label: value})),
    [selectedField]
  );

  const valuesData = useMemo(() => {
    const currentValues = config.to_values ?? [];
    const existingValues = new Set(valueOptions.map((v) => v.value));
    const missing = currentValues.filter((v) => !existingValues.has(v));
    if (missing.length === 0) {
      return valueOptions;
    }
    return [...valueOptions, ...missing.map((value) => ({value, label: value}))];
  }, [valueOptions, config.to_values]);

  const triggerKind = config.trigger_kind;
  // Default to the scope being unselected rather than "all", to avoid a user assuming
  // they should apply this to all of a given target.
  const configuredScope: TriggerScope | undefined =
    config.object_ids != null ? 'instance' : config.schema_id != null ? 'schema' : undefined;
  const capabilities = deriveCapabilities({
    selectedSchemaFolderItemType,
    selectedTrigger,
    triggerKind,
  });

  useEffect(() => {
    if (configuredScope != null) {
      setSelectedScope(configuredScope);
      return;
    }
    if ((config.object_type ?? '') === '' || config.trigger_kind == null) {
      setSelectedScope(undefined);
    }
    // When configuredScope is null but object_type and trigger_kind are set,
    // the user chose "all" — preserve their selection by doing nothing.
  }, [config.object_type, config.trigger_kind, configuredScope]);

  const objectTypeLabel =
    selectedObjectType === '' ? 'Object' : getTitleCasedObjectTypeLabel(selectedObjectType);

  const handleTriggerKindChange = (nextTriggerKind: TriggerKind): void => {
    if (nextTriggerKind === OBJECT_CREATED) {
      if (selectedScope === 'instance') {
        setSelectedScope(undefined);
      }
      onChange({
        trigger_kind: OBJECT_CREATED,
        field: undefined,
        to_values: undefined,
        object_ids: undefined,
      });
      return;
    }
    onChange({
      trigger_kind: FIELD_CHANGED,
      field: config.field ?? '',
      to_values: config.to_values ?? [],
    });
  };

  const handleScopeChange = (nextScope: TriggerScope): void => {
    setSelectedScope(nextScope);
    if (nextScope === 'all') {
      onChange({schema_id: undefined, object_ids: undefined});
      return;
    }
    if (nextScope === 'schema') {
      // The form normalizer clears object_ids when schema_id is updated.
      onChange({schema_id: config.schema_id ?? ''});
      return;
    }
    // The form normalizer clears schema_id when object_ids is updated.
    onChange({object_ids: config.object_ids ?? []});
  };

  const handleObjectTypeChange = (value: string | null): void => {
    setSelectedScope(undefined);
    if (value == null) {
      onChange({
        object_type: '',
        trigger_kind: undefined,
        field: undefined,
        to_values: undefined,
        schema_id: undefined,
        object_ids: undefined,
      });
      return;
    }

    const nextAllowedTrigger = triggerByObjectType.get(value);
    const supportsCreation = nextAllowedTrigger?.supportsCreation ?? false;
    const supportsFieldChanges = (nextAllowedTrigger?.fields.length ?? 0) > 0;
    let nextTriggerKind: TriggerKind | undefined;
    if (supportsCreation && !supportsFieldChanges) {
      nextTriggerKind = OBJECT_CREATED;
    } else if (!supportsCreation && supportsFieldChanges) {
      nextTriggerKind = FIELD_CHANGED;
    }

    const updates: Partial<EventTriggerConfig> = {
      object_type: value,
      trigger_kind: nextTriggerKind,
      schema_id: undefined,
      object_ids: undefined,
    };
    if (nextTriggerKind === OBJECT_CREATED) {
      updates.field = undefined;
      updates.to_values = undefined;
    } else if (nextTriggerKind === FIELD_CHANGED) {
      updates.field = '';
      updates.to_values = [];
    } else {
      updates.field = undefined;
      updates.to_values = undefined;
    }
    onChange(updates);
  };

  const getCardClassName = (selected: boolean, disabled: boolean): string =>
    cx(classes.card, selected && classes.selectedCard, disabled && classes.disabledCard);

  return (
    <Stack spacing="md">
      <Stack spacing={4} className={classes.section}>
        <Text size="sm" weight={600}>
          Data type
        </Text>
        <Text size="xs" color="dimmed">
          Which data type should trigger this routine?
        </Text>
        <Select
          placeholder="Select data type"
          value={config.object_type ?? ''}
          onChange={handleObjectTypeChange}
          data={objectTypeData}
          classNames={{dropdown: classes.selectDropdown}}
          size="sm"
        />
      </Stack>

      <EventTypeSection
        capabilities={capabilities}
        triggerKind={triggerKind}
        objectTypeLabel={objectTypeLabel}
        config={config}
        fieldOptions={fieldOptions}
        valuesData={valuesData}
        getCardClassName={getCardClassName}
        nestedEventInputContainerClassName={classes.nestedEventInputContainer}
        onChange={onChange}
        onTriggerKindChange={handleTriggerKindChange}
        sectionClassName={classes.section}
        selectDropdownClassName={classes.selectDropdown}
      />

      <TargetScopeSection
        capabilities={capabilities}
        scope={selectedScope}
        objectTypeLabel={objectTypeLabel}
        pluralObjectTypeLabel={selectedPluralObjectTypeLabel}
        selectedObjectType={selectedObjectType}
        selectedObjectSearchType={selectedObjectSearchType}
        selectedSchemaFolderItemType={selectedSchemaFolderItemType}
        config={config}
        getCardClassName={getCardClassName}
        onChange={onChange}
        onScopeChange={handleScopeChange}
        searchInputContainerClassName={classes.searchInputContainer}
        sectionClassName={classes.section}
      />
    </Stack>
  );
}
